import { supabase } from '../lib/supabase'
import { logError, logInfo } from './logger'

export async function uploadBoardThumbnail(
  userId: string,
  boardId: string,
  file: Blob
): Promise<string | null> {
  try {
    // Use ISO timestamp for sortable filenames (e.g. 2025-11-24T10-00-00-000Z_thumbnail.png)
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
    const fileName = `${timestamp}_thumbnail.png`
    const filePath = `${userId}/${boardId}/${fileName}`

    const { error: uploadError } = await supabase.storage
      .from('thumbnails')
      .upload(filePath, file, {
        contentType: 'image/png',
        upsert: true,
        cacheControl: '86400',
      })

    if (uploadError) {
      logError('supabaseStorage', 'Failed to upload thumbnail', {
        uploadError,
        userId,
        boardId,
      })
      return null
    }

    const { data: publicUrlData, error: publicUrlError } = supabase.storage
      .from('thumbnails')
      .getPublicUrl(filePath)

    if (publicUrlError || !publicUrlData?.publicUrl) {
      logError('supabaseStorage', 'Failed to get thumbnail public URL', {
        publicUrlError,
        userId,
        boardId,
        filePath,
      })
      return null
    }

    logInfo('supabaseStorage', 'Thumbnail uploaded successfully', {
      userId,
      boardId,
      filePath,
    })

    return publicUrlData.publicUrl
  } catch (error) {
    logError('supabaseStorage', 'Unexpected error uploading thumbnail', {
      error,
      userId,
      boardId,
    })
    return null
  }
}

export async function deleteBoardThumbnail(
  publicUrl: string
): Promise<boolean> {
  try {
    const url = new URL(publicUrl)
    const pathParts = url.pathname.split('/thumbnails/')[1]
    if (!pathParts) {
      logError('supabaseStorage', 'Invalid thumbnail URL for deletion', {
        publicUrl,
      })
      return false
    }

    const { error } = await supabase.storage
      .from('thumbnails')
      .remove([pathParts])

    if (error) {
      logError('supabaseStorage', 'Failed to delete thumbnail', {
        error,
        publicUrl,
      })
      return false
    }

    logInfo('supabaseStorage', 'Thumbnail deleted successfully', {
      publicUrl,
    })

    return true
  } catch (error) {
    logError('supabaseStorage', 'Unexpected error deleting thumbnail', {
      error,
      publicUrl,
    })
    return false
  }
}
